import React, { useEffect, useState } from 'react';
import GlobalLayout from 'components/common/general/layouts/GlobalLayout';
import EmployeeProfile from 'components/subscriber/employee-profile';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import EmployerProfile from 'components/self-enroll/EmployerProfile';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { employeeRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import {
  NoSidebarPageLayout,
  OverlaySpinner,
  PageHeader,
  useFirstRender,
  useQueryParams,
} from '@insurancemenu/imenu-components-lib';
import { buildGroupContextMoreInfoBlobProps } from 'components/common/group/GroupContextMoreInfoBlob';
import { buildGroupContactInfoMap } from 'global/services-helpers/groupService';
import EmployerSidebar from 'components/self-enroll/EmployerSidebar';
import EmployeeHeaderSubtitle from 'components/subscriber/employee-profile/EmployeeHeaderSubtitle';
import EEGroupTabs from './EEGroupTabs';
import { RightSectionStyled } from './styles';

const EmployeeProfilePage = observer((): JSX.Element => {
  const query = useQueryParams();
  const history = useHistory();
  const firstRender = useFirstRender();
  const {
    groupInfoController: { requestInitialData },
    subscriberStore: { getGroupsList, activeGroup, groups, switchActiveGroupTabAndRefetch },
    userStore: { userRoles, overwriteHeaderDataDirty },
  } = useRootStore();

  const convertedRoles = userRoles.map((role) => toJS(role)).slice();
  const convertedActiveGroup = toJS(activeGroup);
  const convertedGroups = groups.map((group) => toJS(group)).slice();

  const [loading, setLoading] = useState(false);

  const groupId = query.get('groupId') || '';
  const isEmployee = employeeRights(convertedRoles);

  const updateHeaderInfo = async (): Promise<void> => {
    await axios.post(
      `https://${process.env.REACT_APP_API_BASE_URL}/aaa/Prod/setSelfEnrollAffiliation`,
      {
        groupId: convertedActiveGroup.id,
      },
      { withCredentials: true },
    );
    await overwriteHeaderDataDirty();
    history.push(`/employee?groupId=${convertedActiveGroup.id}`);
  };

  const getInitialData = async (): Promise<void> => {
    setLoading(true);
    await getGroupsList(groupId);
    if (convertedActiveGroup.id) {
      await updateHeaderInfo();
      // Update data in groupInfoController. Without it, changes with enrollment periods will not be updated properly
      await requestInitialData(convertedActiveGroup.id, false);
    }
    setLoading(false);
  };

  const updateActiveGroupData = async (): Promise<void> => {
    setLoading(true);
    await updateHeaderInfo();
    // update group info in groupInfoController. Without it, changes will not appear at once
    await requestInitialData(convertedActiveGroup.id, false);
    setLoading(false);
  };

  useEffect(() => {
    void getInitialData();
  }, []);

  useEffect(() => {
    if (convertedActiveGroup.id && !firstRender) {
      void updateActiveGroupData();
    }
  }, [convertedActiveGroup.id]);

  const handleSelectGroup = (selectedGroupId: string): void => {
    if (selectedGroupId !== convertedActiveGroup.id) {
      switchActiveGroupTabAndRefetch(selectedGroupId);
      history.push(`/employee?groupId=${selectedGroupId}`);
    }
  };

  if (isEmployee) {
    return (
      <NoSidebarPageLayout
        contentWidth="narrow"
        isInitialLoading={loading}
        isInfoSectionLoading={loading}
        moreInfoBlob={buildGroupContextMoreInfoBlobProps({
          blobTitle: convertedActiveGroup.name || '',
          generalInfoMap: { group: convertedActiveGroup.name || '' },
          contactInfoMap: buildGroupContactInfoMap(convertedActiveGroup.contactInfo ?? null),
        })}
        pageHeader={{
          title: 'Benefits Dashboard',
          subtitle: <EmployeeHeaderSubtitle />,
          subtitleMaxWidth: '80rem',
          spacing: 'withBreadcrumbsSection',
        }}
      >
        <EEGroupTabs groups={convertedGroups} activeGroup={convertedActiveGroup} onSelectGroup={handleSelectGroup}>
          <EmployeeProfile />
        </EEGroupTabs>
      </NoSidebarPageLayout>
    );
  }

  return (
    <>
      <OverlaySpinner display={loading} />
      <GlobalLayout
        leftSection={loading ? null : <EmployerSidebar />}
        rightSection={
          loading ? null : (
            <RightSectionStyled>
              <PageHeader title="Employer Dashboard" />
              <EEGroupTabs
                groups={convertedGroups}
                activeGroup={convertedActiveGroup}
                onSelectGroup={handleSelectGroup}
              >
                <EmployerProfile />
              </EEGroupTabs>
            </RightSectionStyled>
          )
        }
      />
    </>
  );
});

export default EmployeeProfilePage;
